import { appQueryParams } from '../appParams'
import { lastConnectOptions } from '../appStatus'
import styles from './appStatus.module.css'
import Button from './Button'
import Screen from './Screen'
import LoadingTimer from './LoadingTimer'
import { withInjectableUi } from './extendableSystem'
import { PlayIcon } from './components/replay/PlayerIcons'
import Spinner from './Spinner'

const AppStatusBase = ({
  status,
  isError,
  hideDots = false,
  lastStatus = '',
  backAction = undefined as undefined | (() => void),
  description = undefined as undefined | string | JSX.Element,
  actionsSlot = null as React.ReactNode | null,
  showReconnect = false,
  onReconnect = undefined as undefined | (() => void),
  showPlayButton = false,
  onPlay = undefined as undefined | (() => void),
  isStartingPlayback = false,
  children
}) => {
  const lockConnect = appQueryParams.lockConnect === 'true'
  const connectOptions = lastConnectOptions.value
  // loading is spinner-only: no status text, description or timer
  const isLoading = !isError && !showPlayButton
  const showSpinner = isLoading && !hideDots

  return (
    <div className=''>
      <Screen
        className={`small-content ${styles.appStatusScreen} ${showPlayButton || isLoading ? styles.bare : ''} app-status-screen`}
        titleSelectable={isError}
        title={
          <>
            <div className={styles.statusLine}>
              {showPlayButton
                ? (isStartingPlayback
                  ? <Spinner className={styles.loadingSpinner} />
                  : <PlayButton onClick={onPlay} />)
                : isError
                  ? <span className={styles.appStatusTitleError}>{status}</span>
                  : showSpinner && <Spinner className={styles.loadingSpinner} style={spinPhase()} />}
            </div>
            {!isLoading && description && <p className={styles.description}>{description}</p>}
            {!isLoading && !!lastStatus && <p className={styles['last-status']}>{`Last status: ${lastStatus}`}</p>}
            {isError && <p className={`app-status-title-context-info ${styles.appStatusTitleContextInfo}`}>
              S: {connectOptions?.server ?? 'N/A'} {' '}
              V: {connectOptions?.botVersion ?? 'auto'} {' '}
              U: {connectOptions?.username ?? 'N/A'}{' '}
              cV: {process.env.RELEASE_TAG ?? 'N/A'}
            </p>}
          </>
        }
        backdrop='dirt'
      >
        {isError && (
          <>
            {showReconnect && onReconnect && <Button onClick={onReconnect}>
              <b>Reconnect</b>
            </Button>}
            {actionsSlot}
            {!lockConnect && <Button
              onClick={() => {
                if (location.search) {
                  location.search = ''
                } else {
                  window.location.reload()
                }
              }}
            >
              <b>Reset App (recommended)</b>
            </Button>}
            {backAction && <Button label="Back" onClick={backAction} />}
          </>
        )}
        {children}
        {!isLoading && <LoadingTimer />}
      </Screen>
    </div>
  )
}

/* The index.html loader has been spinning since page load and stays visible through
   its 0.2s fade-out; without this the two arcs overlap at different angles. */
const spinPhase = () => ({ animationDelay: `-${Number(document.timeline.currentTime ?? 0) % 900}ms` })

/* Same glyph and fill as the player bar's play control, just scaled up to be
   the one thing on screen. */
const PlayButton = ({ onClick }: { onClick?: () => void }) => (
  <button type="button" className={styles.playButton} onClick={onClick} aria-label="Играть" title="Играть">
    <span className={styles.playButtonIcon}>
      <PlayIcon size="100%" />
    </span>
  </button>
)

export default withInjectableUi(AppStatusBase, 'appStatus')
